import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { apiClient, formatApiError } from "@/lib/api";
import { imgUrl } from "@/utils/format";
import { Camera, Loader2, X, Upload } from "lucide-react";

/**
 * Reusable image uploader for profile pictures & restaurant photos.
 *
 * Props:
 *  - value: current image URL (string) or null
 *  - onChange: (newUrl | null) => void
 *  - label: optional label above
 *  - hint: optional helper text
 *  - shape: "circle" | "rounded" (default "circle" for avatars)
 *  - size: pixel size (default 96)
 *  - allowRemove: bool
 *  - testId: root data-testid
 */
export default function ProfilePictureUploader({
  value,
  onChange,
  label,
  hint,
  shape = "circle",
  size = 96,
  allowRemove = true,
  testId = "profile-picture",
}) {
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const pick = () => fileRef.current?.click();

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 6 * 1024 * 1024) {
      toast.error("Image trop volumineuse (max 6 Mo)");
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await apiClient.post("/uploads/image", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange?.(data.url);
      toast.success("Photo importée");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const clear = () => onChange?.(null);
  const radius = shape === "circle" ? "rounded-full" : "rounded-2xl";

  return (
    <div className="inline-flex items-center gap-4" data-testid={testId}>
      <div className="relative shrink-0">
        <button
          type="button"
          onClick={pick}
          disabled={uploading}
          data-testid={`${testId}-thumb`}
          aria-label="Changer la photo"
          style={{ width: size, height: size }}
          className={`${radius} bg-elevated border-2 border-sand hover:border-brand-red transition-colors overflow-hidden grid place-items-center relative disabled:opacity-70`}
        >
          {value ? (
            <img src={imgUrl(value)} alt="Photo" className="w-full h-full object-cover" />
          ) : (
            <Camera className="w-6 h-6 text-mute" />
          )}
          {uploading && (
            <div className="absolute inset-0 bg-black/40 grid place-items-center">
              <Loader2 className="w-5 h-5 text-white animate-spin" />
            </div>
          )}
        </button>
        {value && allowRemove && !uploading && (
          <button
            type="button"
            onClick={clear}
            data-testid={`${testId}-remove`}
            aria-label="Retirer la photo"
            className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-brand-red text-white grid place-items-center shadow"
          >
            <X className="w-3 h-3" />
          </button>
        )}
        <input ref={fileRef} type="file" accept="image/*" onChange={handleFile} className="hidden" data-testid={`${testId}-input`} />
      </div>
      <div>
        {label && <div className="text-sm font-semibold">{label}</div>}
        {hint && <div className="text-xs text-mute">{hint}</div>}
        <button
          type="button"
          onClick={pick}
          disabled={uploading}
          data-testid={`${testId}-btn`}
          className="mt-1 inline-flex items-center gap-1.5 text-xs text-brand-red font-semibold hover:underline"
        >
          <Upload className="w-3 h-3" /> {value ? "Changer la photo" : "Importer une photo"}
        </button>
      </div>
    </div>
  );
}
